// @file Whiteboard color utility functions for Tailwind CSS classes

import { tailwindFlattenedColors } from '@@/bits/tailwind_config'
import type {
  TldrawColorVariantType,
  TldrawDefaultColorStyleColor,
  ToolTailwindColorType,
} from '@@/bits/whiteboard_tools'
import { TOOL_COLOR_TO_TLDRAW_DEFAULT_COLOR_STYLE_COLOR } from '@@/bits/whiteboard_tools'
import { DefaultColorThemePalette } from 'tldraw'

const TOOL_COLOR_TO_TAILWIND_BG: Record<ToolTailwindColorType, string> = {
  'neon-red': 'bg-neon-red',
  'neon-pink': 'bg-neon-pink',
  'neon-yellow': 'bg-neon-yellow',
  'neon-orange': 'bg-neon-orange',
  'neon-green': 'bg-neon-green',
  'neon-teal': 'bg-neon-teal',
  'neon-blue': 'bg-neon-blue',
  'neon-purple': 'bg-neon-purple',
  'neon-white': 'bg-neon-white',
  'grey-200': 'bg-grey-200',
  'grey-400': 'bg-grey-400',
  'scarlet-500': 'bg-scarlet-500',
  'scarlet-300': 'bg-scarlet-300',
  'scarlet-100': 'bg-scarlet-100',
  'tangerine-500': 'bg-tangerine-500',
  'tangerine-300': 'bg-tangerine-300',
  'tangerine-100': 'bg-tangerine-100',
  'whiteboard-shape-border-yellow': 'bg-whiteboard-shape-border-yellow',
  'canary-200': 'bg-canary-200',
  'canary-100': 'bg-canary-100',
  'park-500': 'bg-park-500',
  'park-200': 'bg-park-200',
  'park-100': 'bg-park-100',
  'teal-500': 'bg-teal-500',
  'teal-200': 'bg-teal-200',
  'teal-100': 'bg-teal-100',
  'whiteboard-shape-border-blue': 'bg-whiteboard-shape-border-blue',
  'oceanic-200': 'bg-oceanic-200',
  'oceanic-100': 'bg-oceanic-100',
  'grape-500': 'bg-grape-500',
  'grape-200': 'bg-grape-200',
  'grape-100': 'bg-grape-100',
  'padlet-pink-500': 'bg-padlet-pink-500',
  'padlet-pink-100': 'bg-padlet-pink-100',
  'dark-text-100': 'bg-dark-text-100',
  'light-text-100': 'bg-light-text-100',
  'light-ui-300': 'bg-light-ui-300',
  'whiteboard-shape-fill-padlet-pink': 'bg-whiteboard-shape-fill-padlet-pink',
  'grey-50': 'bg-grey-50',
}

const TOOL_COLOR_TO_TAILWIND_TEXT: Record<ToolTailwindColorType, string> = {
  'neon-red': 'text-neon-red',
  'neon-pink': 'text-neon-pink',
  'neon-yellow': 'text-neon-yellow',
  'neon-orange': 'text-neon-orange',
  'neon-green': 'text-neon-green',
  'neon-teal': 'text-neon-teal',
  'neon-blue': 'text-neon-blue',
  'neon-purple': 'text-neon-purple',
  'neon-white': 'text-neon-white',
  'grey-200': 'text-grey-200',
  'grey-400': 'text-grey-400',
  'scarlet-500': 'text-scarlet-500',
  'scarlet-300': 'text-scarlet-300',
  'scarlet-100': 'text-scarlet-100',
  'tangerine-500': 'text-tangerine-500',
  'tangerine-300': 'text-tangerine-300',
  'tangerine-100': 'text-tangerine-100',
  'whiteboard-shape-border-yellow': 'text-whiteboard-shape-border-yellow',
  'canary-200': 'text-canary-200',
  'canary-100': 'text-canary-100',
  'park-500': 'text-park-500',
  'park-200': 'text-park-200',
  'park-100': 'text-park-100',
  'teal-500': 'text-teal-500',
  'teal-200': 'text-teal-200',
  'teal-100': 'text-teal-100',
  'whiteboard-shape-border-blue': 'text-whiteboard-shape-border-blue',
  'oceanic-200': 'text-oceanic-200',
  'oceanic-100': 'text-oceanic-100',
  'grape-500': 'text-grape-500',
  'grape-200': 'text-grape-200',
  'grape-100': 'text-grape-100',
  'padlet-pink-500': 'text-padlet-pink-500',
  'padlet-pink-100': 'text-padlet-pink-100',
  'dark-text-100': 'text-dark-text-100',
  'light-text-100': 'text-light-text-100',
  'light-ui-300': 'text-light-ui-300',
  'whiteboard-shape-fill-padlet-pink': 'text-whiteboard-shape-fill-padlet-pink',
  'grey-50': 'text-grey-50',
}

/**
 * Type guard to check if a string is a valid ToolTailwindColorType
 */
export function isValidToolColor(color: string): color is ToolTailwindColorType {
  return color in TOOL_COLOR_TO_TAILWIND_BG
}

export function getTailwindBgColorClass(toolId: ToolTailwindColorType): string | null {
  return TOOL_COLOR_TO_TAILWIND_BG[toolId] ?? null
}

export function getTailwindColorClass(toolId: ToolTailwindColorType): string | null {
  return TOOL_COLOR_TO_TAILWIND_TEXT[toolId] ?? null
}

// Map extra colors to the closest tldraw default color style color
// This is for Jamboard shapes color mapping
const EXTRA_COLOR_TO_TLDRAW_DEFAULT_COLOR_STYLE_COLOR: Record<
  TldrawColorVariantType,
  Partial<Record<string, TldrawDefaultColorStyleColor>>
> = {
  highlight: {
    'grey-200': 'black', // Jamboard shape border color
    'canary-500': 'light-red',
  },
  solid: {},
  geo: {},
  note: {},
  semi: {
    'oceanic-300': 'blue', // Jamboard shape fill color
    'grape-100': 'violet',
    'park-100': 'green',
    // 'grey-200': 'white',
  },
}

type SolidColor = string
interface HighlightColor {
  srgb: string
  p3: string
}
interface NoteColor {
  fill: string
  text: string
}

function changeTldrawDefaultColorStyleColor(
  color: string,
  variant: 'solid' | 'highlight' | 'note' | 'semi',
  toolColor: ToolTailwindColorType,
): void {
  // For darkmode, we are setting to the same color as light mode since we're ignoring dark mode for now in v1
  // TODO: Add dark mode colorset
  for (const mode of ['lightMode', 'darkMode'] as const) {
    let targetColor: SolidColor | HighlightColor | NoteColor = '#000000'

    if (variant === 'solid' || variant === 'semi') {
      targetColor = tailwindFlattenedColors[toolColor]
    }
    if (variant === 'highlight') {
      targetColor = {
        srgb: tailwindFlattenedColors[toolColor],
        p3: tailwindFlattenedColors[toolColor],
      }
    }
    if (variant === 'note') {
      targetColor = {
        fill: tailwindFlattenedColors[toolColor],
        text: '#000000',
      }
    }
    DefaultColorThemePalette[mode][color][variant] = targetColor
  }
}

export function changeAllTldrawDefaultColorStyleColor(): void {
  const variants = Object.keys(TOOL_COLOR_TO_TLDRAW_DEFAULT_COLOR_STYLE_COLOR) as TldrawColorVariantType[]

  variants.forEach((variant) => {
    const colors = TOOL_COLOR_TO_TLDRAW_DEFAULT_COLOR_STYLE_COLOR[variant]
    const tailwindColors = Object.keys(colors) as ToolTailwindColorType[]

    tailwindColors.forEach((tailwindColor) => {
      const tldrawColor = colors[tailwindColor]
      if (tldrawColor == null) {
        return
      }
      changeTldrawDefaultColorStyleColor(tldrawColor, variant as 'solid' | 'highlight' | 'note' | 'semi', tailwindColor)
    })

    // This is a special case for the yellow (whiteboard-shape-border-yellow) color, as in SOLID_COLOR_MAP, it maps to light-red, abut in GEO_AND_CONNECTORS_COLOR_MAP, it maps to 'yellow'.
    // IN TLDraw, all shapes use the "solid" variant for the border color.
    // https://github.com/tldraw/tldraw/blob/main/packages/tldraw/src/lib/shapes/geo/components/GeoShapeBody.tsx#L36
    if (variant === 'solid') {
      DefaultColorThemePalette.lightMode.yellow.solid = tailwindFlattenedColors['whiteboard-shape-border-yellow']
      DefaultColorThemePalette.darkMode.yellow.solid = tailwindFlattenedColors['whiteboard-shape-border-yellow']
    }
  })

  const variantsForExtraColors = Object.keys(
    EXTRA_COLOR_TO_TLDRAW_DEFAULT_COLOR_STYLE_COLOR,
  ) as TldrawColorVariantType[]
  variantsForExtraColors.forEach((variant) => {
    const colors = EXTRA_COLOR_TO_TLDRAW_DEFAULT_COLOR_STYLE_COLOR[variant]
    const tailwindColors = Object.keys(colors) as ToolTailwindColorType[]
    tailwindColors.forEach((tailwindColor) => {
      const tldrawColor = colors[tailwindColor]
      if (tldrawColor == null) {
        return
      }
      changeTldrawDefaultColorStyleColor(tldrawColor, variant as 'solid' | 'highlight' | 'note' | 'semi', tailwindColor)
    })
  })
}
